// frontend/src/hooks/usePermissions.ts

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Permissions } from '../utils/permissions';

export interface UsePermissionsReturn {
  canViewClients: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canViewBookings: boolean;
  canEditBookings: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  userRole: string | undefined;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const userRole = user?.role;

  const permissions = useMemo(() => {
    return {
      canViewClients: Permissions.canViewClients(userRole),
      canCreate: Permissions.canCreate(userRole),
      canEdit: Permissions.canEdit(userRole),
      canDelete: Permissions.canDelete(userRole),
      canViewBookings: Permissions.canViewBookings(userRole),
      canEditBookings: Permissions.canEditBookings(userRole),
      canManageUsers: Permissions.canManageUsers(userRole),
      canManageSettings: Permissions.canManageSettings(userRole),
      isAdmin: Permissions.isAdmin(userRole),
      isManager: Permissions.isManager(userRole),
      isStaff: Permissions.isStaff(userRole),
      userRole,
    };
  }, [userRole]);

  return permissions;
};

export const useHasPermission = (
  permission: keyof Omit<UsePermissionsReturn, 'userRole'>
): boolean => {
  const permissions = usePermissions();
  return permissions[permission];
};
